import { CSSProperties } from 'react';
import { useGenericBi } from '../../lib/use-generic-bi';

// The corner launcher FAB — the closed-chat affordance. On wide it opens the
// side dock; on narrow (MobileChatLauncher) it opens the input-only composer.
const chatFabStyle: CSSProperties = {
  position: 'fixed',
  // Ride left of the open task/goal side panel (its reserved footprint) so the
  // launcher isn't hidden behind it — reflows in lockstep with the content pane.
  insetInlineEnd: 'calc(var(--sidepanel-w, 0px) + 20px)',
  // Rides ABOVE a mobile action bar (e.g. the Maestro page's sticky Save) when one is present, so the
  // launcher never sits on top of it — `--mobile-action-bar-h` is that bar's height, set on the root
  // while it is mounted and 0 (unset) everywhere else.
  bottom: 'calc(20px + var(--mobile-action-bar-h, 0px))',
  transition: 'inset-inline-end 420ms cubic-bezier(0.32, 0.72, 0, 1), bottom 260ms ease',
  zIndex: 50,
  width: 56,
  height: 56,
  padding: 0,
  border: 'none',
  background: 'var(--ds-surface-sunken)',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  boxShadow: '0 2px 10px rgba(var(--ds-black-rgb), 0.14)',
  animation: 'orion-bubble-in 260ms cubic-bezier(0.16, 1, 0.3, 1)',
  // No grey overlay flash when the FAB is tapped on a phone.
  WebkitTapHighlightColor: 'transparent',
};

// Matches the native apps' chat FAB asset
// (native-apps/ios/.../chat_fab_icon.imageset/chat_fab_icon.png): a solid, wide
// oval speech bubble with a tail flicking down to the lower-left.
function BubbleGlyph() {
  return (
    <svg width="21" height="21" viewBox="0 0 22 22" fill="none" aria-hidden>
      <path
        d="M11 1C16.52 1 21 4.7 21 9.3C21 13.9 16.52 17.6 11 17.6C9.7 17.6 8.45 17.42 7.28 17.1C6.1 18.5 4.4 19.75 2.5 20.4C2.02 20.56 1.63 20.05 1.86 19.6C2.6 18.3 3.3 17.1 3.5 16.05C1.95 14.48 1 12 1 9.3C1 4.7 5.48 1 11 1Z"
        fill="var(--ds-surface-sunken-dark)"
      />
    </svg>
  );
}

/**
 * The dot the FAB wears while the Maestro is mid-turn.
 *
 * The FAB is the closed-chat affordance, so it is exactly where "something is happening in there"
 * belongs: the user stepped out of the chat, and this is the door back in. It is a presence mark, not a
 * count — one turn is not a number, and a "1" would read as an unread message.
 */
const workingDotStyle: CSSProperties = {
  position: 'absolute',
  top: 6,
  insetInlineEnd: 6,
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: 'var(--ds-badge-fill-in-progress)',
  boxShadow: '0 0 0 2px var(--ds-white)',
};

export function ChatFab({ onClick, label, working, workingLabel }: { onClick: () => void; label: string; working?: boolean; workingLabel?: string }) {
  const { logPageClickBi } = useGenericBi();
  return (
    <button
      type="button"
      onClick={() => {
        logPageClickBi({ page_name: 'chat', button_name: 'open_chat' });
        onClick();
      }}
      // The label CHANGES while it is working, rather than the dot being the only signal — a coloured
      // dot says nothing to a screen reader, and "the Maestro is working" is the whole message.
      aria-label={working && workingLabel ? workingLabel : label}
      style={{ ...chatFabStyle, position: 'fixed' }}
    >
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        <BubbleGlyph />
        {working ? <span style={workingDotStyle} aria-hidden /> : null}
      </span>
    </button>
  );
}
